import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { computed, inject, provide, ref, watch, type InjectionKey } from 'vue'
import { useRoute, type RouteRecordNameGeneric } from 'vue-router'

const ROUTE_TITLES: Partial<Record<string, string>> = {
  dashboard: 'Dashboard',
  tasks: 'Tasks',
  'task-detail': 'Task',
  'task-editor': 'Edit Task',
  'task-new': 'New Task',
  runs: 'Runs',
  'run-detail': 'Run',
  connectors: 'Connectors',
  'data-explorer': 'Data Explorer',
  triggers: 'Triggers',
  'job-templates': 'Templates',
  'job-template-new': 'New Template',
  'job-template-editor': 'Edit Template',
  'job-template-detail': 'Template',
  'dashboard-template-new': 'New Dashboard',
  'dashboard-template-editor': 'Edit Dashboard',
  jobs: 'Jobs',
  'job-detail': 'Job',
  'dashboard-detail': 'Dashboard',
  'job-run-detail': 'Job Run',
  users: 'Users',
  executors: 'Executors',
  settings: 'Settings',
  'ai-references': 'AI References',
  'ai-reference-new': 'New Reference',
  'ai-reference-editor': 'Edit Reference',
}

type AppLayoutContext = ReturnType<typeof createAppLayoutState>

const APP_LAYOUT_KEY: InjectionKey<AppLayoutContext> = Symbol('appLayout')

const FULL_HEIGHT_ROUTE_NAMES = new Set([
  'task-editor',
  'task-new',
  'data-explorer',
  'job-template-new',
  'job-template-editor',
  'dashboard-template-new',
  'dashboard-template-editor',
  'ai-reference-new',
  'ai-reference-editor',
])

function createAppLayoutState() {
  const route = useRoute()
  const breakpoints = useBreakpoints(breakpointsTailwind)
  const isDesktop = breakpoints.greaterOrEqual('lg')
  const mobileNavOpen = ref(false)

  const pageTitle = computed(() => {
    const name = route.name as RouteRecordNameGeneric
    if (typeof name === 'string' && ROUTE_TITLES[name]) return ROUTE_TITLES[name]
    return 'Envoy'
  })

  const pagePaddingClass = 'px-4 pt-4 md:px-6 md:pt-6 lg:px-8 lg:pt-8 lg:pb-12'

  const isFullHeightRoute = computed(() => {
    const name = route.name as RouteRecordNameGeneric
    return typeof name === 'string' && FULL_HEIGHT_ROUTE_NAMES.has(name)
  })

  // Bottom breathing room for mobile/tablet scroll pages - a real DOM box, since
  // padding on the overflow:auto scroll container is unreliable at scroll end on
  // mobile WebKit. Also clears the iOS home-indicator safe area. Desktop relies
  // on lg:pb-12 in pagePaddingClass instead. Full-height routes manage their
  // own internal scrolling, so they should not lose viewport height to a spacer.
  const pageEndSpacerClass = computed(() =>
    isFullHeightRoute.value
      ? 'hidden'
      : 'h-[max(3rem,calc(env(safe-area-inset-bottom,0px)+1.5rem))] lg:hidden',
  )

  watch(
    () => route.path,
    () => {
      mobileNavOpen.value = false
    },
  )

  function openMobileNav() {
    mobileNavOpen.value = true
  }

  function closeMobileNav() {
    mobileNavOpen.value = false
  }

  return {
    isDesktop,
    isFullHeightRoute,
    mobileNavOpen,
    pageTitle,
    pagePaddingClass,
    pageEndSpacerClass,
    openMobileNav,
    closeMobileNav,
  }
}

export function provideAppLayout() {
  const layout = createAppLayoutState()
  provide(APP_LAYOUT_KEY, layout)
  return layout
}

export function useAppLayout() {
  const layout = inject(APP_LAYOUT_KEY)
  if (!layout) {
    throw new Error('useAppLayout must be used within a component that calls provideAppLayout')
  }
  return layout
}
